// Phase 45.5.3 — <MediaCard /> (Plan 45.5.3-02 Task 1, CANVAS-15).
//
// One v1-style SMALL CARD for the canvas media grid, mirroring v1's
// `SortableImageTile` (BoatMediaManager.tsx:172-250): a rounded-border `bg-card`
// tile with a compact `h-28` thumbnail area and a filename + size footer
// (Phase 45.5.3 refresh shrank the v1 `h-40` to fit a denser 4-up grid).
// Replaces the
// flat `<li>` rows of UploadedFilesStrip — the hero (CanvasHeroIngest, Task 3)
// renders a grid of these for the union of STAGED items (useCombinedUpload) and
// COMMITTED items (Phase 45.5.3 review: derived in LiveFillCanvas from the
// useBoatMedia(boatId) query — the single source of truth — and mapped into the
// committed card shape; the grid self-heals on mutation via the query refetch).
//
// The card is driven by a NORMALIZED UNION prop (RESEARCH Open Q #5 (a)):
//   - 'staged'    → a CombinedUploadItem (no server mediaId; image or document;
//                   carries a localPreview blob URL). Pre-commit Move/Remove.
//   - 'committed' → a TempMediaItem (always an image per the gallery hook;
//                   carries a server mediaId + url). ⭐ set-featured + Delete.
//
// THUMBNAIL resolution (mirrors DocumentCard.DocumentThumbnail for the PDF path):
//   - committed, or a staged image → <img> (object-cover); src is item.url
//     (committed) or item.localPreview (staged image).
//   - staged PDF document → renderFirstPage(localPreview) gated behind an
//     IntersectionObserver (lazy; pitfall #5), rendered as the returned PNG
//     data-URL, falling back to a <FileText/> icon while loading / on null.
//   - other staged document (non-PDF) → a file-type icon by MIME.
//
// SAFETY / THREAT MODEL (D-4, Pitfall 2): no new endpoint. ⭐ reuses
// useSetPrimaryBoatImage and Delete reuses useDeleteBoatMedia — BOTH gated to
// `kind === 'committed'` so a staged clientId (no server mediaId) can never hit
// the set-primary / delete endpoints. `renderFirstPage` fetches the PDF via the
// credentialed session (no raw MinIO URL held) and returns null on any failure
// so a hostile/corrupt PDF degrades to an icon, never crashes. Filenames render
// as escaped React text children — never dangerouslySetInnerHTML.
//
// SELF-HEAL (Phase 45.5.3 review): the committed ⭐/Delete mutations are the
// SINGLE source of truth — they invalidate boatMediaKeys.detail(boatId), and the
// canvas grid is sourced from the SAME useBoatMedia(boatId) query (see
// LiveFillCanvas / CanvasHeroIngest), so the invalidation refetch removes the
// deleted card and moves the ⭐ automatically. There is NO local-snapshot
// reconciliation callback (the removed CR-01/CR-02 band-aid) — this card just
// fires the mutation, exactly like v1's BoatMediaManager SortableImageTile.
//
// Reorder is OUT of scope (O-1) — the dnd-kit grip from SortableImageTile is
// intentionally dropped.

import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  File as FileIcon,
  FileLock,
  FileText,
  Image as ImageIcon,
  Lock,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import {
  useDeleteBoatMedia,
  useSetPrimaryBoatImage,
} from '@/modules/inventory/hooks/useBoatMedia';
import { renderFirstPage } from '../documents/pdfThumbnail';
import type { CombinedUploadItem } from '../documents/hooks/useCombinedUpload';
// D-15: type-only import of the committed-media item shape — no boat-form
// runtime is pulled in.
import type { TempMediaItem } from './types';
// Phase 45.5.3 (CANVAS — committed-documents on resume): the factory-packet
// document row shape from the Documents-tab query. Committed DOCUMENTS live in a
// SEPARATE store from committed images (useBoatMedia returns only { images,
// videos }), so the canvas sources them from useDocuments(boatId) and maps each
// BoatDocument into the third union arm below. Type-only import.
import type { BoatDocument } from '../documents/hooks/useDocuments';
// #1219 — a held AI-intake image (not in the gallery until the classifier routes it).
import type { PendingBoatImage } from '@/modules/inventory/services/boatMediaApi';
import { PendingImageActions } from '../media/PendingImageActions';
import { deleteImageWithToast } from '../media/deleteImageWithToast';
import { PendingImageThumbnail } from '../media/PendingImageThumbnail';
import { useFailedReadMediaIds } from './CopilotSessionContext';

/**
 * The normalized union the grid host (Task 3) maps each source item into. A
 * staged item is a pre-commit upload (no server identity yet); a committed image
 * is a persisted gallery image with a server mediaId; a committed document is a
 * persisted private factory-packet file (BoatDocument) with its own auth-gated
 * download stream.
 */
export type MediaCardItem =
  | { kind: 'staged'; item: CombinedUploadItem }
  | { kind: 'committed'; item: TempMediaItem }
  | { kind: 'committed-document'; item: BoatDocument }
  | { kind: 'pending-image'; item: PendingBoatImage };

interface MediaCardProps {
  /** The normalized staged-or-committed entry this card renders. */
  entry: MediaCardItem;
  /**
   * The live boat id. Required for the committed actions (⭐ / Delete) to fire;
   * when null those mutations are guarded (no server id to target).
   */
  boatId: string | null;
  /** Whether this committed image is the gallery's primary (renders the ⭐ Featured badge + disables ⭐). */
  isPrimary?: boolean;
  /** Pre-commit bucket re-route (image↔document) for a STAGED item. */
  onReroute?: (clientId: string) => void;
  /** Discard a STAGED item from the batch (the parent revokes its blob URL). */
  onRemoveStaged?: (clientId: string) => void;
  /**
   * Delete a COMMITTED DOCUMENT (kind === 'committed-document'). The host wires
   * this to useDocuments(boatId).destroy.mutate({ mediaId }); its onSuccess
   * invalidates documentKeys.lists() so the query refetches and this card
   * self-heals away — exactly like the committed-image Delete path. Distinct
   * from committed images (which delete via useDeleteBoatMedia internally)
   * because documents live in a separate store with its own mutation.
   */
  onDeleteDocument?: (mediaId: number) => void;
  /** True while a commit is in flight — dims the staged Move/Remove controls. */
  committing?: boolean;
}

function formatSize(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Humanized doc_type → label for the committed-document badge. Mirrors
 * DocumentCard.DOC_TYPE_LABELS but is inlined here so MediaCard does not import
 * the heavy DocumentCard module (Dialog/Dropdown/AlertDialog trees) just for a
 * label string. A document with no doc_type falls back to the generic "Document"
 * badge (so the card always carries a private-file indicator).
 */
const DOC_TYPE_BADGE_LABELS: Record<string, string> = {
  invoice: 'Invoice',
  coo_boat: 'COO (boat)',
  coo_trailer: 'COO (trailer)',
  bill_of_lading: 'Bill of lading',
  bill_of_sale: 'Bill of sale',
  survey: 'Survey',
  registration: 'Registration',
  insurance: 'Insurance',
  service_record: 'Service record',
  recall_notice: 'Recall notice',
  warranty: 'Warranty',
  other: 'Document',
};

/**
 * One media small-card. Chrome mirrors v1's SortableImageTile (rounded border,
 * compact `h-28` thumbnail, footer) minus the drag grip. The thumbnail + badges + footer
 * actions branch on `entry.kind`.
 */
export function MediaCard({
  entry,
  boatId,
  isPrimary = false,
  onReroute,
  onRemoveStaged,
  onDeleteDocument,
  committing = false,
}: MediaCardProps) {
  const { mutate: setPrimary } = useSetPrimaryBoatImage();
  const { mutate: deleteMedia } = useDeleteBoatMedia();

  // Normalize filename + size PER KIND: committed images + staged items carry
  // camelCase `fileName`/`size`; a committed document is a raw BoatDocument with
  // snake_case `file_name`/`size`. Reading `entry.item.fileName` blindly (the
  // prior code) returned undefined for documents, so derive per arm.
  const fileName =
    entry.kind === 'committed-document' || entry.kind === 'pending-image'
      ? entry.item.file_name
      : entry.item.fileName;
  const size = entry.item.size;
  // Placement badge: staged documents and committed documents are PRIVATE
  // (never published to the website gallery); images (staged + committed) are
  // Public. Committed documents additionally render a doc-type badge below.
  const isPrivate =
    entry.kind === 'committed-document' ||
    entry.kind === 'pending-image' ||
    (entry.kind === 'staged' && entry.item.bucket === 'document');

  // #1178 (spec §2) — a partial run failure shows on the file itself.
  const failedReads = useFailedReadMediaIds();
  const serverMediaId =
    entry.kind === 'committed'
      ? String(entry.item.mediaId)
      : entry.kind === 'committed-document' || entry.kind === 'pending-image'
        ? String(entry.item.id)
        : null;
  const readFailed = serverMediaId !== null && failedReads.has(serverMediaId);

  return (
    <div
      data-testid="media-card"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="relative h-28 bg-muted">
        <MediaThumbnail entry={entry} />

        {/* Public / Private badge — top-left, like v1's Featured badge slot. */}
        {isPrivate ? (
          <Badge
            variant="secondary"
            appearance="light"
            className="absolute left-2 top-2"
          >
            <Lock className="size-3" aria-hidden="true" />
            Private
          </Badge>
        ) : (
          <Badge
            variant="info"
            appearance="light"
            className="absolute left-2 top-2"
          >
            Public
          </Badge>
        )}

        {/* Committed-document doc-type badge — stacked below the Private badge so
            both stay visible (a document is never the gallery ⭐, so this slot is
            free). Falls back to a generic "Document" label when untyped. */}
        {entry.kind === 'committed-document' && (
          <Badge
            variant="secondary"
            size="sm"
            className="absolute left-2 top-9"
            data-testid="doc-type-badge"
          >
            {DOC_TYPE_BADGE_LABELS[entry.item.doc_type ?? 'other'] ?? 'Document'}
          </Badge>
        )}

        {/* #1219 — a held AI-intake image: private until the classifier routes it
            (or the dealer confirms it). Stacked below the Private badge. */}
        {entry.kind === 'pending-image' && (
          <Badge
            variant="warning"
            appearance="light"
            size="sm"
            className="absolute left-2 top-9"
            data-testid="pending-badge"
          >
            Not yet classified
          </Badge>
        )}

        {readFailed && (
          <Badge
            variant="destructive"
            appearance="light"
            size="sm"
            className="absolute right-2 top-2"
            data-testid="read-failed-badge"
          >
            Couldn’t read
          </Badge>
        )}

        {/* ⭐ Featured badge on the primary committed image (top-left, stacked
            below the placement badge so both stay visible). */}
        {entry.kind === 'committed' && isPrimary && (
          <Badge
            variant="success"
            size="sm"
            className="absolute left-2 top-9"
            data-testid="featured-badge"
          >
            Featured
          </Badge>
        )}

        {/* Per-item state overlays for STAGED items (lifted from
            UploadedFilesStrip): a progress bar while staging, a destructive
            ring + AlertCircle on error. */}
        {entry.kind === 'staged' && entry.item.staging && (
          <StagingProgress progress={entry.item.progress} />
        )}
        {entry.kind === 'staged' && entry.item.error && (
          <ErrorOverlay message={entry.item.error} />
        )}
      </div>

      <div className="flex items-center justify-between gap-2 p-2">
        <div className="min-w-0">
          <p className="truncate text-xs text-foreground" title={fileName}>
            {fileName}
          </p>
          <p className="text-[11px] leading-tight text-muted-foreground">
            {formatSize(size)}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {entry.kind === 'staged' && (
            <StagedActions
              item={entry.item}
              committing={committing}
              onReroute={onReroute}
              onRemoveStaged={onRemoveStaged}
            />
          )}
          {entry.kind === 'committed-document' && (
            <CommittedDocumentActions
              item={entry.item}
              onDelete={() => {
                // Self-heal: the host wires onDeleteDocument to
                // useDocuments.destroy.mutate({ mediaId }); its onSuccess
                // invalidates documentKeys.lists() so the query refetches and
                // this card disappears — same fire-and-forget contract as the
                // committed-image Delete (no local snapshot to reconcile).
                onDeleteDocument?.(entry.item.id);
              }}
            />
          )}
          {entry.kind === 'pending-image' && (
            // #1219 — Use as photo / Sort again / Delete. Each action refetches
            // boatMediaKeys.detail(boatId), whose response carries
            // `pending_images`, so the card self-heals away.
            <PendingImageActions image={entry.item} boatId={boatId} />
          )}
          {entry.kind === 'committed' && (
            <CommittedActions
              item={entry.item}
              boatId={boatId}
              isPrimary={isPrimary}
              onSetPrimary={() => {
                // Pitfall 2: a committed item carries a server mediaId; the null
                // boatId guard keeps the mutate from firing without a target.
                if (!boatId) return;
                const mediaId = entry.item.mediaId;
                setPrimary(
                  { boatId, mediaId },
                  {
                    // Self-heal: useSetPrimaryBoatImage invalidates
                    // boatMediaKeys.detail(boatId); the grid is sourced from that
                    // same useBoatMedia query, so the refetch re-orders the list
                    // (new primary → images[0]) and the ⭐ badge moves on its own.
                    // No local reconciliation — same contract as BoatMediaManager.
                    onSuccess: () => toast.success('Featured image updated.'),
                    onError: () =>
                      toast.error('Failed to update featured image.'),
                  },
                );
              }}
              onDelete={() => {
                if (!boatId) return;
                // Self-heal: useDeleteBoatMedia invalidates boatMediaKeys.detail(boatId);
                // the query-backed grid refetches and the deleted card disappears.
                deleteImageWithToast(deleteMedia, boatId, entry.item.mediaId);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Thumbnail — image <img> / PDF first-page / doc icon                        */
/* -------------------------------------------------------------------------- */

function MediaThumbnail({ entry }: { entry: MediaCardItem }) {
  // #1219 — a held image previews through its auth-gated stream (never a storage
  // URL). F5 — that stream can 404/401/error; PendingImageThumbnail falls back
  // to the same icon fallback the document thumbnails already use instead of a
  // browser broken-image icon.
  if (entry.kind === 'pending-image') {
    return <PendingImageThumbnail item={entry.item} />;
  }

  // Committed items are always images (the gallery hook) — render item.url.
  if (entry.kind === 'committed') {
    return (
      <img
        src={entry.item.url}
        alt={entry.item.fileName}
        className="h-full w-full object-cover"
        loading="lazy"
      />
    );
  }

  // Committed DOCUMENT — a private factory-packet file from useDocuments. A PDF
  // gets a BEST-EFFORT first-page preview off its auth-gated download_url
  // (IntersectionObserver-gated, with a FileText fallback when the credentialed
  // stream can't render — corrupt/forbidden/non-renderable). Anything else gets
  // a file-type icon by mime_type. The card still shows filename + doc badge +
  // download even when the preview degrades to the icon.
  if (entry.kind === 'committed-document') {
    if (entry.item.mime_type === 'application/pdf') {
      return <PdfThumbnail url={entry.item.download_url} />;
    }
    return (
      <div className="flex h-full w-full items-center justify-center">
        <DocumentIcon mimeType={entry.item.mime_type} />
      </div>
    );
  }

  // Staged image — render the local blob preview.
  if (entry.item.bucket === 'image') {
    return (
      <img
        src={entry.item.localPreview}
        alt={entry.item.fileName}
        className="h-full w-full object-cover"
      />
    );
  }

  // Staged PDF document — lazy first-page render (IntersectionObserver-gated).
  if (entry.item.mimeType === 'application/pdf') {
    return <PdfThumbnail url={entry.item.localPreview} />;
  }

  // Other staged document (non-PDF) — a file-type icon by MIME.
  return (
    <div className="flex h-full w-full items-center justify-center">
      <DocumentIcon mimeType={entry.item.mimeType} />
    </div>
  );
}

/**
 * A PDF first-page thumbnail rendered lazily via `renderFirstPage`, copied from
 * DocumentCard.DocumentThumbnail (DocumentCard.tsx:116-165): an
 * IntersectionObserver flips `requested` on intersect, a useEffect renders the
 * page into a PNG data-URL, and a <FileText/> icon shows while loading / on a
 * null (failed/corrupt) render.
 */
function PdfThumbnail({ url }: { url: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (requested) return;
    const node = ref.current;
    if (!node) return;

    // On-demand render only when the card scrolls into view (pitfall #5).
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setRequested(true);
        observer.disconnect();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [requested]);

  useEffect(() => {
    if (!requested) return;
    let active = true;
    void renderFirstPage(url).then((dataUrl) => {
      if (active) setThumb(dataUrl);
    });
    return () => {
      active = false;
    };
  }, [requested, url]);

  return (
    <div ref={ref} className="flex h-full w-full items-center justify-center">
      {thumb ? (
        <img src={thumb} alt="" className="h-full w-full object-cover" />
      ) : (
        <FileText className="size-8 text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  );
}

function DocumentIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') {
    return <FileText className="size-8 text-muted-foreground" aria-hidden="true" />;
  }
  if (mimeType.startsWith('image/')) {
    return <ImageIcon className="size-8 text-muted-foreground" aria-hidden="true" />;
  }
  return <FileIcon className="size-8 text-muted-foreground" aria-hidden="true" />;
}

/* -------------------------------------------------------------------------- */
/* Footer actions                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Staged-item actions: a Move (pre-commit reroute, image↔document) + a Remove,
 * both calling e.stopPropagation() (mirror UploadedFilesStrip.MoveButton — WR-01
 * defense-in-depth) and disabled while a commit is in flight.
 */
function StagedActions({
  item,
  committing,
  onReroute,
  onRemoveStaged,
}: {
  item: CombinedUploadItem;
  committing: boolean;
  onReroute?: (clientId: string) => void;
  onRemoveStaged?: (clientId: string) => void;
}) {
  const toImages = item.bucket === 'document';
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={committing}
        onClick={(e) => {
          e.stopPropagation();
          onReroute?.(item.clientId);
        }}
        aria-label={toImages ? 'Move to images' : 'Move to documents'}
        title={
          toImages
            ? 'Publish this to the website gallery'
            : "Keep this private — don't publish to the website"
        }
      >
        {toImages ? (
          <ImageIcon className="size-4" aria-hidden="true" />
        ) : (
          <FileLock className="size-4" aria-hidden="true" />
        )}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={committing}
        onClick={(e) => {
          e.stopPropagation();
          onRemoveStaged?.(item.clientId);
        }}
        aria-label="Remove from this batch"
        title="Remove from this batch"
      >
        <X className="size-4 text-destructive" aria-hidden="true" />
      </Button>
    </>
  );
}

/**
 * Committed-item actions: a ⭐ set-featured (disabled when already primary, like
 * v1 SortableImageTile) + a Delete. Both fire the server mutations the host
 * wired in (gated on a non-null boatId at the call site).
 */
function CommittedActions({
  item: _item,
  boatId,
  isPrimary,
  onSetPrimary,
  onDelete,
}: {
  item: TempMediaItem;
  boatId: string | null;
  isPrimary: boolean;
  onSetPrimary: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        // Disabled when already featured (the fill is the affordance) OR when
        // there is no boatId to target the server call.
        disabled={isPrimary || !boatId}
        onClick={(e) => {
          e.stopPropagation();
          onSetPrimary();
        }}
        aria-label={isPrimary ? 'Featured image' : 'Set as featured'}
        className={cn(isPrimary && 'opacity-100 disabled:opacity-100')}
      >
        <Star
          className={cn(
            'size-4',
            isPrimary && 'fill-yellow-400 text-yellow-400',
          )}
          aria-hidden="true"
        />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={!boatId}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete image"
      >
        <Trash2 className="size-4 text-destructive" aria-hidden="true" />
      </Button>
    </>
  );
}

/**
 * Committed-DOCUMENT actions: a Download + a Delete. NO ⭐ set-primary and NO
 * reroute/replace — documents are private files, not gallery images, and
 * re-typing/replacing is the DocumentsTab's job (this canvas grid only needs to
 * make a resumed draft's documents VISIBLE + openable + removable).
 *
 * Download is a real anchor navigation to the auth-gated download_url (the
 * session cookie authorizes the stream per request — the client never holds a
 * raw MinIO URL, threat T-45-06b). `?download=1` forces an attachment, mirroring
 * DocumentCard.handleDownload. Rendering an <a> (not a button) gives a genuine
 * link affordance the dealer can open in a new tab.
 *
 * Delete fires the host's onDeleteDocument (→ useDocuments.destroy.mutate) whose
 * onSuccess invalidates documentKeys.lists() so the query-backed grid refetches
 * and this card self-heals away — the same fire-and-forget self-heal as the
 * committed-image Delete.
 */
function CommittedDocumentActions({
  item,
  onDelete,
}: {
  item: BoatDocument;
  onDelete: () => void;
}) {
  return (
    <>
      <Button
        asChild
        size="sm"
        variant="ghost"
        aria-label={`Download ${item.file_name}`}
      >
        <a
          href={`${item.download_url}?download=1`}
          target="_blank"
          rel="noopener"
          download
          onClick={(e) => e.stopPropagation()}
        >
          <Download className="size-4" aria-hidden="true" />
        </a>
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label={`Delete ${item.file_name}`}
      >
        <Trash2 className="size-4 text-destructive" aria-hidden="true" />
      </Button>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Staged-item state overlays (lifted from UploadedFilesStrip)                */
/* -------------------------------------------------------------------------- */

/** Bottom progress bar over the thumbnail while a staged item uploads. */
function StagingProgress({ progress }: { progress: number }) {
  return (
    <>
      <div className="absolute inset-0 bg-black/30" />
      <div
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        className="absolute bottom-0 left-0 right-0 h-1 bg-black/40"
      >
        <div
          className="h-full bg-primary transition-[width] duration-150 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </>
  );
}

/** Destructive ring + AlertCircle over an errored staged item. */
function ErrorOverlay({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-destructive/20 px-2 text-center ring-2 ring-destructive ring-inset"
    >
      <AlertCircle className="size-6 text-destructive" aria-hidden="true" />
      <span className="text-xs text-destructive">{message}</span>
    </div>
  );
}
